import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Facebook } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "contact", data: form });
      setSent(true);
      toast.success("Your message has been sent");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="bg-ivory">
      <section className="border-b border-line py-16 md:py-24 text-center">
        <Reveal>
          <p className="eyebrow">Contact</p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">We'd Love to Hear From You</h1>
          <p className="mx-auto mt-6 max-w-md text-sm md:text-base text-stone2">Questions, wholesale, collaborations — or just to tell us which scent you burned through first.</p>
        </Reveal>
      </section>
      <section className="mx-auto max-w-5xl px-6 sm:px-12 py-16 md:py-24 grid gap-16 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <h2 className="font-serif text-3xl font-light tracking-tight">Merari Candle Company</h2>
          <p className="mt-4 text-sm text-stone2 leading-relaxed">Hand-poured in Manayunk, Philadelphia.<br />Poured with love, shipped with care.</p>
          <div className="mt-8 space-y-3 text-sm">
            <p><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 block">Email</span> hello@meraricandleco.com</p>
            <p><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 block">Hours</span> Monday - Friday, 9am - 5pm ET</p>
          </div>
          <div className="mt-8 flex gap-3">
            <a href="https://instagram.com/meraricandleco" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="flex h-10 w-10 items-center justify-center border border-line bg-white transition-colors duration-300 hover:bg-ink hover:text-ivory"><Instagram size={16} strokeWidth={1.5} /></a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="flex h-10 w-10 items-center justify-center border border-line bg-white transition-colors duration-300 hover:bg-ink hover:text-ivory"><Facebook size={16} strokeWidth={1.5} /></a>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-3">
          {sent ? (
            <div className="border border-line bg-white p-12 text-center" data-testid="contact-success">
              <p className="font-serif text-3xl font-light italic">Message received.</p>
              <p className="mt-4 text-sm text-stone2">We'll reply within 1-2 business days.</p>
              <button onClick={() => { setSent(false); setForm(EMPTY); }} data-testid="contact-send-another" className="btn-outline mt-8">Send Another</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-line bg-white p-8 md:p-10 grid gap-6">
              <label className="block"><span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">Name</span>
                <input required data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" /></label>
              <label className="block"><span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">Email</span>
                <input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" /></label>
              <label className="block"><span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">Subject</span>
                <input data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} placeholder="What's on your mind?" /></label>
              <label className="block"><span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">Message</span>
                <textarea required rows="5" data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder="Tell us everything…" /></label>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary w-full disabled:opacity-50">{loading ? "Sending…" : "Send Message"}</button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
